import { forwardRef, ReactNode } from 'react';
import { Checkbox as BuiCheckbox } from '@backstage/ui';

import styles from './Checkbox.module.css';

/**
 * First argument of `onChange`. It has the shape of a DOM change event
 * (`event.target.checked`), so handlers written for the MUI checkbox keep
 * working; the new state is also the second argument.
 */
export type CheckboxChangeEvent = {
  target: {
    name: string;
    checked: boolean;
    value?: string;
  };
};

export interface CheckboxProps {
  name: string;
  checked: boolean;
  /** Visible label next to the box. */
  children?: ReactNode;
  /** Accessible name when there is no visible label; defaults to `name`. */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  value?: string;
  disabled?: boolean;
  indeterminate?: boolean;
  className?: string;
  /** Defaults to `name`. */
  'data-testid'?: string;
  /**
   * Called with the new state. Declared as a method so handlers typed for
   * DOM change events stay assignable.
   */
  onChange?(event: CheckboxChangeEvent, checked: boolean): void;
}

/**
 * Checkbox on the Backstage UI `Checkbox`. The label is the child; a checkbox
 * without one gets `aria-label` or, as a fallback, its `name`.
 */
export const Checkbox = forwardRef<HTMLLabelElement, CheckboxProps>(
  (props, ref) => {
    const {
      name,
      checked,
      children,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      value,
      disabled,
      indeterminate,
      className,
      'data-testid': testId,
      onChange,
    } = props;

    const hasLabel = children !== undefined && children !== null;

    return (
      <BuiCheckbox
        ref={ref}
        name={name}
        value={value}
        isSelected={checked}
        isDisabled={disabled}
        isIndeterminate={indeterminate}
        aria-label={
          ariaLabel ?? (hasLabel || ariaLabelledBy ? undefined : name)
        }
        aria-labelledby={ariaLabelledBy}
        className={[styles.checkbox, 'xlrt-Checkbox', className]
          .filter(Boolean)
          .join(' ')}
        data-testid={testId ?? name}
        onChange={isSelected =>
          onChange?.(
            { target: { name, checked: isSelected, value } },
            isSelected,
          )
        }
      >
        {children}
      </BuiCheckbox>
    );
  },
);

Checkbox.displayName = 'Checkbox';
